declare const window: any;

import {RFEModuleUi, RFEModuleUiInstance} from '../helpers/module';

/**
 * This module is used for the functionality of smooth scrolling to selected anchor or element.
 */
export default class SmoothScroll extends RFEModuleUi {

    /**
     * Selects and returns related HTML elements
     * @param   {HTMLElement}                       context     Parent element for searching target elements (HTMLElement)
     * @return  {(NodeList | Array<HTMLElement>)}               NodeList or array of HTML elements
     */
    getUiElements(context: HTMLElement): NodeList | Array<HTMLElement> {
        const links = context.querySelectorAll("a[href^='#']");
        const elements: NodeList | Array<HTMLElement> = [];

        for (let i = 0; i < links.length; i++) {
            const link = links[i] as HTMLElement;
            const href = link.getAttribute('href') as string;

            if (link.getAttribute('data-disable-smooth-scroll') || !href || href.length < 2 || href.indexOf('#') !== 0) {
                continue; // not valid or excluded anchor link => skip it
            }
            elements.push(link); // adds to the array with filtered items
        }

        return elements;
    }

    /**
     * Creates single Ui instance from DOM element
     * @param   {HTMLElement}       el   DOM object from witch we want to create new instance (HTMLElement)
     * @return  {SmoothScrollUi}         module instance (RFEModuleUiInstance)
     */
    createUiInstance(el: HTMLElement): SmoothScrollUi {
        return new SmoothScrollUi(this, el);
    }
}

// Ui for one instance of module
class SmoothScrollUi extends RFEModuleUiInstance {
    private steps: number = 50; // how many steps per duration time?
    private topOffset: number = 150; // offset from top of window [px]
    private duration: number = 500; // time of animation [ms]
    private iterationTimeout: any;

    /**
     * Initialization of single instance of the module
     * @return  {boolean}      TRUE/FALSE if the initialization was successful or not (boolean)
     */
    public init(): boolean {
        this.el.addEventListener('click', this.anchorLinkClick, false);

        // the instance of module has been initialized => return TRUE
        return true;
    }

    /**
     * De-initialization of single instance of the module
     */
    public deinit(): void {
        this.el.removeEventListener('click', this.anchorLinkClick, false);
        clearTimeout(this.iterationTimeout);
    }

    /**
     * It will do smooth scroll animation to selected DOM element.
     * @param   {HTMLElement}   element   Target element (HTMLElement).
     */
    public scrollToElement = (element: HTMLElement): void => {
        if (element) {
            this.animate(window.pageYOffset, RFE.getOffsetFromBody(element).y - this.topOffset, this.steps);
        }
    }

    /**
     * Animation of smooth scrolling.
     * @param   {number}   from    Start vertical position of animation. (number)
     * @param   {number}   to      End vertical position of animation. (number)
     * @param   {number}   steps   Number of steps. (number)
     */
    private animate = (from: number, to: number, steps: number): void => {
        const piece: number = (to - from) / steps;
        let i: number = 0;
        const iteration = () => {
            const pos: number = from + (i * piece);
            document.documentElement.scrollTop = pos;
            document.body.scrollTop = pos;
            if (++i <= steps) {
                this.iterationTimeout = setTimeout(iteration, this.duration / (steps));
            }
        };
        iteration();
    }

    /**
     * Click event on anchor link. It will create smooth scroll animation.
     * @return  {(boolean | void)}      Returns false to prevent click action (boolean)
     */
    private anchorLinkClick = (): boolean | void => {
        const link: HTMLElement = this.el;
        if (!link) {
            return;
        }
        const href = link.getAttribute('href') as string;
        const name = href.substr(1) as string;
        let target = document.getElementById(name) as HTMLElement;

        if (!target) { // try to find <a> element with related 'name' attribute
            const anchors = document.getElementsByTagName('a');
            for (let k = 0; k < anchors.length; k++) {
                let anchor: any = anchors[k];
                if (anchor.name === name) {
                    target = anchor;
                }
            }
        }

        if (target) {
            this.scrollToElement(target);
        }

        // event tracking
        // if (name === 'comments') {
        //     trackAnalyticsEvent({ on_page_event: 'smooth_scroll-#' + name });
        // }

        return false;
    }
}



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./source/js/modules/smooth_scroll.ts